import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Drawer } from "antd";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import Loader from "../../components/Common/Loader";
import { nutrientUnits } from "../../utils/Constant/Constant";

const RecipeDrawer = ({ recipeDetails, visible, onClose }) => {
  const { trans } = useCustomTranslation();
  const [loadingImage, setLoadingImage] = useState(false);

  const hasRecipe = !!recipeDetails;
  const recipeData = useMemo(() => recipeDetails || {}, [recipeDetails]);

  const MISSING_NUTRIENT_FALLBACK = "0";

  const formatValueWithUnit = (
    val,
    unit,
    fallback = MISSING_NUTRIENT_FALLBACK
  ) => {
    if (val === null || val === undefined) return `${fallback} ${unit}`;
    const strVal = String(val).trim();
    if (strVal === "" || strVal.toLowerCase() === "nan")
      return `${fallback} ${unit}`;
    if (/[a-zA-Z]/.test(strVal)) return strVal; // already has a unit
    return `${strVal} ${unit}`;
  };

  // Compute macro split
  const macroRatios = useMemo(() => {
    const carbsRaw =
      recipeData?.nutrientsPerserving?.carbs_g ||
      recipeData?.nutrientsPerserving?.carbs;
    const proteinRaw =
      recipeData?.nutrientsPerserving?.protein_g ||
      recipeData?.nutrientsPerserving?.protein;
    const fatRaw =
      recipeData?.nutrientsPerserving?.fat_g ||
      recipeData?.nutrientsPerserving?.fat;

    const carbsGrams = parseFloat(carbsRaw) || 0;
    const proteinGrams = parseFloat(proteinRaw) || 0;
    const fatGrams = parseFloat(fatRaw) || 0;

    const totalCalories = proteinGrams * 4 + carbsGrams * 4 + fatGrams * 9;
    if (!totalCalories) return { carbs: 0, protein: 0, fat: 0 };

    return {
      carbs: (carbsGrams * 4) / totalCalories,
      protein: (proteinGrams * 4) / totalCalories,
      fat: (fatGrams * 9) / totalCalories,
    };
  }, [recipeData]);

  const dotColor = {
    carbs: "#89008e",
    protein: "#574dec",
    fat: "#dceb00",
  };

  const formatNutrientKey = (key) =>
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());

  return (
    <Drawer
      title={<div></div>}
      placement="right"
      width="30%"
      onClose={onClose}
      open={visible}
      className="p-3"
    >
      {!hasRecipe ? (
        <Loader />
      ) : (
        <div>
          {/* Image */}
          {recipeData?.mainImage && (
            <img
              className="image-card-drawer"
              alt={recipeData.title || "Recipe"}
              src={recipeData.mainImage}
              onLoad={() => setLoadingImage(false)}
              onError={() => setLoadingImage(false)}
              style={{ display: loadingImage ? "none" : "block" }}
            />
          )}

          {/* Title */}
          <h3 className="drawer-recipe-name text-gray-700">
            {recipeData?.title}
          </h3>

          <div className="detail-container-drawer">
            <hr />
            {/* Time / Servings / Ingredients */}
            <div className="d-flex">
              <p className="detail-time-recipe">
                {(recipeData?.prepTime || recipeData?.totalTime) ?? "N/A"}
              </p>
              <p className="detail-time-recipe">
                {`${recipeData?.servings ?? 0}`}
                {/* {`${recipeData?.servings ?? 0} ${trans("recipes.servings")}`} */}
              </p>
              <p className="detail-time-recipe">
                {`${recipeData?.ingredientsCount ?? 0} ${trans(
                  "recipes.ingredients"
                )}`}
              </p>
            </div>

            <hr className="mt-0" />

            {/* Ingredients */}
            <h4>{trans("recipes.Ingredients")}</h4>
            <ul className="blue-dot-list">
              {(recipeData?.ingredientList || []).map((item, idx) => {
                return (
                  <li key={idx} className="fs-5">
                    {`${item.quantityMetric} ${item.unitMetric} ${
                      item?.ingredient || " "
                    }`}
                  </li>
                );
              })}
            </ul>

            {/* Instructions */}
            <h4>{trans("recipes.instructions")}</h4>
            <ul className="blue-dot-list">
              {(recipeData?.instructions || []).flatMap((instr, idx) =>
                instr
                  .split(",,")
                  .filter((step) => step.trim().length > 0)
                  .map((step, stepIdx) => (
                    <li key={`${idx}-${stepIdx}`} className="fs-5">
                      {formatNutrientKey(step.trim())}
                    </li>
                  ))
              )}
            </ul>

            <hr />
            {/* Nutrition */}
            <h4>{trans("recipes.nutritionPerServing")}</h4>

            {/* Macro progress bar */}
            <div className="mt-2 progress-bar-recipe mb-3">
              <div
                className="bar-carb"
                style={{ width: `${(macroRatios.carbs || 0) * 100}%` }}
              />
              <div
                className="bar-protein"
                style={{ width: `${(macroRatios.protein || 0) * 100}%` }}
              />
              <div
                className="bar-fat"
                style={{ width: `${(macroRatios.fat || 0) * 100}%` }}
              />
            </div>

            {/* Macro indicators */}
            {["carbs", "protein", "fat"].map((macro) => (
              <React.Fragment key={macro}>
                <div className="indicator-nutrition d-flex align-items-center">
                  <div
                    className="rounded-circle ml-2 mr-2"
                    style={{
                      backgroundColor: dotColor[macro],
                      width: 15,
                      height: 15,
                    }}
                  />
                  <span className="fs-5">
                    {trans(`recipes.${macro}`)} (
                    {((macroRatios?.[macro] || 0) * 100).toFixed(2)}%)
                  </span>
                  <p className="ml-auto mb-0">
                    {formatValueWithUnit(
                      recipeData?.nutrientsPerserving?.[`${macro}_g`] ||
                        recipeData?.nutrientsPerserving?.[macro],
                      "g"
                    )}
                  </p>
                </div>
                <hr />
              </React.Fragment>
            ))}
            <ul>
              {Object.entries(recipeData?.nutrientsPerserving || {})
                .filter(([key]) =>
                  [
                    "calories",
                    "calcium",
                    "iron",
                    "magnesium",
                    "potassium",
                    "sodium",
                    "sugar",
                    "vitaminC",
                    "fiber",
                  ].includes(key)
                )
                .map(([key, value]) => {
                  const formattedKey = formatNutrientKey(key);
                  const unit = nutrientUnits[key] || "";
                  return (
                    <li key={key} className="p-0 m-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <p>{formattedKey}</p>
                        <p>{formatValueWithUnit(value, unit)}</p>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      )}
    </Drawer>
  );
};

RecipeDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recipeDetails: PropTypes.object,
};

export default RecipeDrawer;
